/*
 * Copyright (c) Arbeidskracht Groep B.V. - all rights reserved
 */

import {combineEpics, Epic, StateObservable} from 'redux-observable';
import {filter, ignoreElements, map, mapTo, mergeMap, tap} from 'rxjs/operators';
import {TokenType} from '../services/LocalStorage/TokenType';
import {BehaviorSubject, combineLatest, of} from 'rxjs';
import {
	iamStompClient,
	localStorage,
	loginService,
	passwordStrengthResource,
	rememberMeAuthenticationResource,
	twoFactorAuthenticationResource
} from '../services';
import {
	loginSceneAuthenticationAborted,
	loginSceneAuthenticationBlocked,
	loginSceneAuthenticationExpired,
	loginSceneAuthenticationInvalidCredentials,
	loginSceneAuthenticationNotFound,
	loginSceneAuthenticationStarted,
	loginSceneChangePasswordValidationException,
	loginSceneCredentialsSubmitted,
	loginSceneEmailOwnershipDeniedForFlexServiceWorkerRegistration,
	loginSceneEnterPhoneVerificationCodeValidationException,
	loginSceneFlexServiceWorkerRegistrationCompleted,
	loginSceneFlexServiceWorkerRegistrationExpired,
	loginSceneFlexServiceWorkerRegistrationFailed,
	loginSceneNewPasswordInputChanged,
	loginSceneNewPasswordStrengthEvaluated,
	loginSceneNewPasswordSubmitted,
	loginScenePasswordChangeRequired,
	loginScenePasswordNotChangedWithInsufficientStrength,
	loginScenePasswordNotChangedWithSamePassword,
	loginScenePhoneVerificationCodeSubmitted,
	loginScenePhoneVerificationFailed,
	loginScenePhoneVerificationStartBlocked,
	loginScenePhoneVerificationStartBusy,
	loginScenePhoneVerificationStarted,
	loginScenePhoneVerificationStartFailed,
	loginScenePhoneVerificationStopped,
	loginSceneRememberMeTokenNotOk,
	loginSceneRememberMeTokenOk,
	loginSceneRememberMeTokenSubmitted,
	loginSceneRequestRestartPhoneVerification,
	loginSceneRequestRetryPhoneVerification,
	loginSceneResetButtonClicked,
	loginSceneRetryPhoneVerificationUnexpectedError,
	loginSceneStartAuthenticationValidationException
} from '../scenes/Login/actions';
import {isActionOf} from 'typesafe-actions';
import {
	STATE_CHANGE_FOR_FLEX_SERVICE_WORKER_REGISTRATION,
	TWO_FACTOR_AUTHENTICATION_ABORTED,
	TWO_FACTOR_AUTHENTICATION_BLOCKED,
	TWO_FACTOR_AUTHENTICATION_DELEGATED_TO_FLEX_SERVICE_WORKER_REGISTRATION,
	TWO_FACTOR_AUTHENTICATION_EXPIRED,
	TWO_FACTOR_AUTHENTICATION_INVALID_CREDENTIALS_PROVIDED,
	TWO_FACTOR_AUTHENTICATION_PASSWORD_CHANGE_REQUIRED,
	TWO_FACTOR_AUTHENTICATION_PASSWORD_NOT_CHANGED_WITH_INSUFFICIENT_STRENGTH,
	TWO_FACTOR_AUTHENTICATION_PASSWORD_NOT_CHANGED_WITH_SAME_PASSWORD,
	TWO_FACTOR_AUTHENTICATION_PHONE_VERIFICATION_RESPONSE_VALIDATION_FAILED,
	TWO_FACTOR_AUTHENTICATION_PHONE_VERIFICATION_START_BLOCKED,
	TWO_FACTOR_AUTHENTICATION_PHONE_VERIFICATION_START_FAILED,
	TWO_FACTOR_AUTHENTICATION_PHONE_VERIFICATION_STARTED,
	TWO_FACTOR_AUTHENTICATION_PHONE_VERIFICATION_STOPPED,
	TWO_FACTOR_AUTHENTICATION_SECURITY_CONTEXT_AUTHENTICATED,
	TWO_FACTOR_AUTHENTICATION_STOPPED_WITH_SECOND_FACTOR_BUSY
} from '../services/Stomp/EventType';
import {
	iamApiAuthenticationChangePasswordNotFound,
	iamApiAuthenticationChangePasswordValidationException,
	iamApiAuthenticationEnterPhoneVerificationCodeNotFound,
	iamApiAuthenticationEnterPhoneVerificationCodeValidationException,
	iamApiAuthenticationPasswordChanged,
	iamApiAuthenticationPhoneVerificationCodeEntered,
	iamApiAuthenticationRestartPhoneVerificationNotFound,
	iamApiAuthenticationRetryPhoneVerificationNotFound,
	iamApiAuthenticationRetryPhoneVerificationUnexpectedError,
	iamApiAuthenticationStarted,
	iamApiFlexServiceWorkerRegistrationStateRetrieved,
	iamApiPasswordStrengthEvaluated,
	iamApiRememberMeAuthenticationStarted,
	iamApiStartAuthenticationUnexpectedError,
	iamApiStartAuthenticationValidationException,
	iamApiStartRememberMeAuthenticationUnexpectedError,
	iamApiStartRememberMeAuthenticationValidationException
} from '../services/IamApi/actions';
import {stompMessageReceived} from '../services/Stomp/actions';
import {FlexServiceWorkerRegistrationStateDTO} from '../generated/iam-client';
import {violationsWithErrorMessages} from '../model/validation';
import TwoFactorAuthenticationDelegatedToFlexServiceWorkerRegistrationDTO
	from '../services/Stomp/TwoFactorAuthenticationDelegatedToFlexServiceWorkerRegistrationDTO';
import TwoFactorAuthenticationPhoneVerificationStartedDTO from '../services/Stomp/TwoFactorAuthenticationPhoneVerificationStartedDTO';
import {RootState} from '../reducers';
import TwoFactorAuthenticationPhoneVerificationStoppedDTO from '../services/Stomp/TwoFactorAuthenticationPhoneVerificationStoppedDTO';
import TwoFactorAuthenticationPasswordChangeRequiredDTO from '../services/Stomp/TwoFactorAuthenticationPasswordChangeRequiredDTO';
import TwoFactorAuthenticationAccountPasswordNotChangedWithInsufficientStrengthDTO
	from '../services/Stomp/TwoFactorAuthenticationAccountPasswordNotChangedWithInsufficientStrengthDTO';
import {Route as LoginUIRoute} from '../routes';
import {AuthenticationData} from '../services/LoginService/LoginService';
import {navigate} from '../components/OutHook';

const logoutFromQueryString: () => boolean = () => new URLSearchParams(window.document.location.search).get('logout') === 'true' || false;

const startRememberMeAuthenticationOnRememberMeTokenSubmitted: Epic = (action$) => {
	return action$.pipe(
		filter(isActionOf(loginSceneRememberMeTokenSubmitted)),
		mergeMap(() => {
			if (logoutFromQueryString()) {
				localStorage.removeToken(TokenType.REMEMBER_ME, null);

				return of(loginSceneRememberMeTokenNotOk());
			}

			const rememberMeToken = localStorage.getToken(TokenType.REMEMBER_ME, null);

			if (rememberMeToken === undefined)
				return of(loginSceneRememberMeTokenNotOk());

			const accountName = rememberMeToken.accountName;
			const clientToken = localStorage.getToken(TokenType.CLIENT, accountName);

			return rememberMeAuthenticationResource.startRememberMeAuthentication({accountName, clientToken, rememberMeToken});
		})
	);
};

const notifyLoginSceneOfEmailOwnershipDeniedForFlexServiceWorkerRegistrationOnRetrievedState: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiFlexServiceWorkerRegistrationStateRetrieved)),
	filter((action) => action.payload.state.emailOwnershipDenied),
	mapTo(loginSceneEmailOwnershipDeniedForFlexServiceWorkerRegistration())
);

const notifyLoginSceneOfFlexServiceWorkerRegistrationFailedOnRetrievedStateWithRegistrationFailed: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiFlexServiceWorkerRegistrationStateRetrieved)),
	filter((action) => action.payload.state.registrationFailed),
	mapTo(loginSceneFlexServiceWorkerRegistrationFailed())
);

const notifyLoginSceneOfFlexServiceWorkerRegistrationFailedOnStateChangeWithRegistrationFailed: Epic = (action$) => action$.pipe(
	filter(isActionOf(stompMessageReceived)),
	filter((action) => {
		const state: FlexServiceWorkerRegistrationStateDTO = action.payload.eventBody as unknown as FlexServiceWorkerRegistrationStateDTO;
		return action.payload.eventType === STATE_CHANGE_FOR_FLEX_SERVICE_WORKER_REGISTRATION && state.registrationFailed;
	}),
	mapTo(loginSceneFlexServiceWorkerRegistrationFailed())
);

const notifyLoginSceneOfFlexServiceWorkerRegistrationCompletedOnRetrievedStateWithRegistrationCompleted: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiFlexServiceWorkerRegistrationStateRetrieved)),
	filter((action) => action.payload.state.registrationCompleted),
	mapTo(loginSceneFlexServiceWorkerRegistrationCompleted())
);

const notifyLoginSceneOfFlexServiceWorkerRegistrationExpiredOnRetrievedStateWithRegistrationExpired: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiFlexServiceWorkerRegistrationStateRetrieved)),
	filter((action) => action.payload.state.registrationExpired),
	mapTo(loginSceneFlexServiceWorkerRegistrationExpired())
);

const notifyLoginSceneOfFlexServiceWorkerRegistrationExpiredOnStateChangeWithRegistrationExpired: Epic = (action$) => action$.pipe(
	filter(isActionOf(stompMessageReceived)),
	filter((action) => {
		const state: FlexServiceWorkerRegistrationStateDTO = action.payload.eventBody as unknown as FlexServiceWorkerRegistrationStateDTO;
		return action.payload.eventType === STATE_CHANGE_FOR_FLEX_SERVICE_WORKER_REGISTRATION && state.registrationExpired;
	}),
	mapTo(loginSceneFlexServiceWorkerRegistrationExpired())
);

const notifyLoginSceneOfRememberMeUnexpectedError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiStartRememberMeAuthenticationUnexpectedError)),
	mapTo(loginSceneRememberMeTokenNotOk())
);

const notifyLoginSceneOfRememberMeInputValidationError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiStartRememberMeAuthenticationValidationException)),
	mapTo(loginSceneRememberMeTokenNotOk())
);

const notifyLoginSceneOfRememberMeAuthenticationResultRetrieved: Epic = (action$) => {
	return action$.pipe(
		filter(isActionOf(iamApiRememberMeAuthenticationStarted)),
		mergeMap((action) => {
			const authenticationResult = action.payload.result;
			if (!authenticationResult.authenticationSuccessful) {
				localStorage.removeToken(TokenType.REMEMBER_ME, null);
				return of(loginSceneRememberMeTokenNotOk());
			}
			if (!authenticationResult.clientToken)
				throw 'Client token MUST be defined for successful authentication result';

			if (!authenticationResult.rememberMeToken)
				throw 'Remember-me token MUST be defined for successful authentication result';

			if (!authenticationResult.redirectURL)
				throw 'Redirect URL MUST be defined for successful authentication result';

			localStorage.setToken(
				TokenType.CLIENT,
				authenticationResult.clientToken.accountName,
				authenticationResult.clientToken.nonce,
				authenticationResult.clientToken.signature
			);

			localStorage.setToken(
				TokenType.REMEMBER_ME,
				authenticationResult.rememberMeToken.accountName,
				authenticationResult.rememberMeToken.nonce,
				authenticationResult.rememberMeToken.signature
			);

			window.location.href = authenticationResult.redirectURL;

			return of(loginSceneRememberMeTokenOk());
		})
	);
};

const startAuthenticationOnLoginCredentialsSubmitted: Epic = (action$) => {
	return action$.pipe(
		filter(isActionOf(loginSceneCredentialsSubmitted)),
		mergeMap((action) => twoFactorAuthenticationResource.startAuthentication(action.payload.command))
	);
};

const notifyLoginSceneOfCredentialsUnexpectedError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiStartAuthenticationUnexpectedError)),
	mapTo(iamApiStartAuthenticationUnexpectedError())
);

const notifyLoginSceneOfCredentialsValidationError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiStartAuthenticationValidationException)),
	map((action) => {
		const violations = violationsWithErrorMessages(action.payload.violations);

		return loginSceneStartAuthenticationValidationException({
			violations: {
				accountName: violations.accountName || [],
				password: violations.password || []
			}
		});
	})
);

const subscribeToAuthenticationEventsOnAuthenticationStarted: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiAuthenticationStarted)),
	mergeMap((action) =>
		iamStompClient.subscribe('/topic/two-factor-authentication/' + action.payload.twoFactorAuthenticationId)
	)
);

const unsubscribeFromAuthenticationEventsOnAuthenticationDelegatedToFlexServiceWorkerRegistration: Epic = (action$) => action$.pipe(
	filter(isActionOf(stompMessageReceived)),
	filter((action) => action.payload.eventType === TWO_FACTOR_AUTHENTICATION_DELEGATED_TO_FLEX_SERVICE_WORKER_REGISTRATION),
	mergeMap((action) => {
			const delegated: TwoFactorAuthenticationDelegatedToFlexServiceWorkerRegistrationDTO =
				action.payload.eventBody as unknown as TwoFactorAuthenticationDelegatedToFlexServiceWorkerRegistrationDTO;
			return iamStompClient.unsubscribe('/topic/two-factor-authentication/' + delegated.twoFactorAuthenticationId);
		}
	)
);

const notifyLoginSceneOfAuthenticationBlocked: Epic = (action$) => action$.pipe(
	filter(isActionOf(stompMessageReceived)),
	filter((action) => action.payload.eventType === TWO_FACTOR_AUTHENTICATION_BLOCKED),
	mapTo(loginSceneAuthenticationBlocked())
);

const notifyLoginSceneOfInvalidCredentialsProvided: Epic = (action$) => action$.pipe(
	filter(isActionOf(stompMessageReceived)),
	filter((action) => action.payload.eventType === TWO_FACTOR_AUTHENTICATION_INVALID_CREDENTIALS_PROVIDED),
	mapTo(loginSceneAuthenticationInvalidCredentials())
);

const notifyLoginSceneOfAuthenticationStarted: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiAuthenticationStarted)),
	map((action) => loginSceneAuthenticationStarted(action.payload))
);

const notifyLoginSceneOfPhoneVerificationStartFailed: Epic = (action$) => action$.pipe(
	filter(isActionOf(stompMessageReceived)),
	filter((action) => action.payload.eventType === TWO_FACTOR_AUTHENTICATION_PHONE_VERIFICATION_START_FAILED),
	mapTo(loginScenePhoneVerificationStartFailed())
);

const notifyLoginSceneOfPhoneVerificationStartBlocked: Epic = (action$) => action$.pipe(
	filter(isActionOf(stompMessageReceived)),
	filter((action) => action.payload.eventType === TWO_FACTOR_AUTHENTICATION_PHONE_VERIFICATION_START_BLOCKED),
	mapTo(loginScenePhoneVerificationStartBlocked())
);

const restartPhoneVerificationWhenRequested: Epic = (action$, state$: StateObservable<RootState>) => action$.pipe(
	filter(isActionOf(loginSceneRequestRetryPhoneVerification)),
	mergeMap(() => twoFactorAuthenticationResource.retryPhoneVerification(String(state$.value.login.index.twoFactorAuthenticationId)))
);

const notifyLoginSceneOfAuthenticationNotFoundException: Epic = (action$) => action$.pipe(
	filter(isActionOf([
		iamApiAuthenticationRetryPhoneVerificationNotFound,
		iamApiAuthenticationRestartPhoneVerificationNotFound,
		iamApiAuthenticationEnterPhoneVerificationCodeNotFound,
		iamApiAuthenticationChangePasswordNotFound
	])),
	mapTo(loginSceneAuthenticationNotFound())
);

const notifyLoginSceneOfRetryPhoneVerificationUnexpectedError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiAuthenticationRetryPhoneVerificationUnexpectedError)),
	mapTo(loginSceneRetryPhoneVerificationUnexpectedError())
);

const notifyLoginSceneOfPhoneVerificationStarted: Epic = (action$) => action$.pipe(
	filter(isActionOf(stompMessageReceived)),
	filter((action) => action.payload.eventType === TWO_FACTOR_AUTHENTICATION_PHONE_VERIFICATION_STARTED),
	map((action) => {
		const started: TwoFactorAuthenticationPhoneVerificationStartedDTO =
			action.payload.eventBody as unknown as TwoFactorAuthenticationPhoneVerificationStartedDTO;
		return loginScenePhoneVerificationStarted(started);
	})
);

const notifyLoginSceneOfSecondFactorBusy: Epic = (action$) => action$.pipe(
	filter(isActionOf(stompMessageReceived)),
	filter((action) => action.payload.eventType === TWO_FACTOR_AUTHENTICATION_STOPPED_WITH_SECOND_FACTOR_BUSY),
	mapTo(loginScenePhoneVerificationStartBusy())
);

const restartPhoneVerificationOnPhoneVerificationRestartSubmitted: Epic = (action$, state$: StateObservable<RootState>) => action$.pipe(
	filter(isActionOf(loginSceneRequestRestartPhoneVerification)),
	mergeMap(() => twoFactorAuthenticationResource.restartPhoneVerification(String(state$.value.login.index.twoFactorAuthenticationId)))
);

const enterVerificationCodeOnVerificationCodeSubmitted: Epic = (action$, state$: StateObservable<RootState>) => action$.pipe(
	filter(isActionOf(loginScenePhoneVerificationCodeSubmitted)),
	mergeMap((action) => twoFactorAuthenticationResource.enterPhoneVerificationCode(
		String(state$.value.login.index.twoFactorAuthenticationId), action.payload.command
	))
);

const notifyLoginSceneOfVerificationCodeValidationError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiAuthenticationEnterPhoneVerificationCodeValidationException)),
	map((action) => {
		const violations = violationsWithErrorMessages(action.payload.violations);

		return loginSceneEnterPhoneVerificationCodeValidationException({
			violations: {
				verificationCode: violations.verificationCode || []
			}
		});
	})
);

const notifyLoginSceneOfPhoneVerificationResponseValidationFailed: Epic = (action$) => action$.pipe(
	filter(isActionOf(stompMessageReceived)),
	filter((action) => action.payload.eventType === TWO_FACTOR_AUTHENTICATION_PHONE_VERIFICATION_RESPONSE_VALIDATION_FAILED),
	mapTo(loginScenePhoneVerificationFailed())
);

const notifyLoginSceneOfPhoneVerificationStopped: Epic = (action$) => action$.pipe(
	filter(isActionOf(stompMessageReceived)),
	filter((action) => action.payload.eventType === TWO_FACTOR_AUTHENTICATION_PHONE_VERIFICATION_STOPPED),
	map((action) => {
		const verificationStopped: TwoFactorAuthenticationPhoneVerificationStoppedDTO =
			action.payload.eventBody as unknown as TwoFactorAuthenticationPhoneVerificationStoppedDTO;
		return loginScenePhoneVerificationStopped({reason: verificationStopped.reason, blocked: verificationStopped.phoneBlocked});
	})
);

const notifyLoginSceneOfPasswordChangeRequired: Epic = (action$) => action$.pipe(
	filter(isActionOf(stompMessageReceived)),
	filter((action) => action.payload.eventType === TWO_FACTOR_AUTHENTICATION_PASSWORD_CHANGE_REQUIRED),
	map((action) => {
		const changeRequired: TwoFactorAuthenticationPasswordChangeRequiredDTO =
			action.payload.eventBody as unknown as TwoFactorAuthenticationPasswordChangeRequiredDTO;
		return loginScenePasswordChangeRequired(changeRequired);
	})
);

const evaluateNewPasswordStrengthOnChange: Epic = (action$, state$: StateObservable<RootState>) => action$.pipe(
	filter(isActionOf(loginSceneNewPasswordInputChanged)),
	mergeMap((action) => passwordStrengthResource.evaluatePasswordStrength({
		password: action.payload.password,
		specificGuessableInputs: state$.value.login.index.passwordCriteria !== null ?
			state$.value.login.index.passwordCriteria.specificGuessableInputs : []
	}))
);

const notifyLoginSceneOfEvaluatedPasswordStrength: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiPasswordStrengthEvaluated)),
	map((action) => loginSceneNewPasswordStrengthEvaluated(action.payload))
);

const postNewPasswordOnChangePasswordSubmitted: Epic = (action$, state$: StateObservable<RootState>) => action$.pipe(
	filter(isActionOf(loginSceneNewPasswordSubmitted)),
	mergeMap((action) => twoFactorAuthenticationResource.changePassword(
		String(state$.value.login.index.twoFactorAuthenticationId), action.payload.command
	))
);

const notifyLoginSceneOfChangePasswordValidationError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiAuthenticationChangePasswordValidationException)),
	map((action) => {
		const violations = violationsWithErrorMessages(action.payload.violations);

		return loginSceneChangePasswordValidationException({
			violations: {
				password: violations.password || []
			}
		});
	})
);

const notifyLoginSceneOfPasswordNotChangedWithSamePassword: Epic = (action$) => action$.pipe(
	filter(isActionOf(stompMessageReceived)),
	filter((action) => action.payload.eventType === TWO_FACTOR_AUTHENTICATION_PASSWORD_NOT_CHANGED_WITH_SAME_PASSWORD),
	mapTo(loginScenePasswordNotChangedWithSamePassword())
);

const notifyLoginSceneOfPasswordNotChangedWithInsufficientStrength: Epic = (action$) => action$.pipe(
	filter(isActionOf(stompMessageReceived)),
	filter((action) => action.payload.eventType === TWO_FACTOR_AUTHENTICATION_PASSWORD_NOT_CHANGED_WITH_INSUFFICIENT_STRENGTH),
	map((action) => {
		const notChanged: TwoFactorAuthenticationAccountPasswordNotChangedWithInsufficientStrengthDTO =
			action.payload.eventBody as unknown as TwoFactorAuthenticationAccountPasswordNotChangedWithInsufficientStrengthDTO;
		return loginScenePasswordNotChangedWithInsufficientStrength(notChanged);
	})
);

const redirectOnSecurityContextAuthenticated: Epic = (action$) => {
	const authRequestPendingSubject = new BehaviorSubject(false);
	action$.pipe(
		filter(isActionOf(loginScenePhoneVerificationCodeSubmitted))
	).subscribe(() => authRequestPendingSubject.next(true));
	action$.pipe(
		filter(isActionOf(iamApiAuthenticationPhoneVerificationCodeEntered))
	).subscribe(() => authRequestPendingSubject.next(false));
	action$.pipe(
		filter(isActionOf(loginSceneNewPasswordSubmitted))
	).subscribe(() => authRequestPendingSubject.next(true));
	action$.pipe(
		filter(isActionOf(iamApiAuthenticationPasswordChanged))
	).subscribe(() => authRequestPendingSubject.next(false));

	const contextAuthenticatedObservable = action$.pipe(
		filter(isActionOf(stompMessageReceived)),
		filter((action) => action.payload.eventType === TWO_FACTOR_AUTHENTICATION_SECURITY_CONTEXT_AUTHENTICATED)
	);

	return combineLatest([authRequestPendingSubject, contextAuthenticatedObservable])
		.pipe(
			tap(([authRequestPending, contextAuthenticatedEvent]) => {
				if (!authRequestPending) {
					const authenticationData: AuthenticationData = contextAuthenticatedEvent.payload.eventBody as unknown as AuthenticationData;
					loginService.login(authenticationData);
				}
			}),
			ignoreElements()
		);
};

const notifyLoginSceneOfAuthenticationExpired: Epic = (action$) => action$.pipe(
	filter(isActionOf(stompMessageReceived)),
	filter((action) => action.payload.eventType === TWO_FACTOR_AUTHENTICATION_EXPIRED),
	mapTo(loginSceneAuthenticationExpired())
);

const notifyLoginSceneOfAuthenticationAborted: Epic = (action$) => action$.pipe(
	filter(isActionOf(stompMessageReceived)),
	filter((action) => action.payload.eventType === TWO_FACTOR_AUTHENTICATION_ABORTED),
	mapTo(loginSceneAuthenticationAborted())
);

const navigateToResetPasswordOnClick: Epic = (action$) => action$.pipe(
	filter(isActionOf(loginSceneResetButtonClicked)),
	tap(() => navigate(LoginUIRoute.RESET_PASSWORD)),
	ignoreElements()
);

const navigateToFlexServiceWorkerRegistrationOnAuthenticationDelegatedToFlexServiceWorkerRegistration: Epic = (action$) => action$.pipe(
	filter(isActionOf(stompMessageReceived)),
	filter((action) => action.payload.eventType === TWO_FACTOR_AUTHENTICATION_DELEGATED_TO_FLEX_SERVICE_WORKER_REGISTRATION),
	tap((action) => {
		const delegated: TwoFactorAuthenticationDelegatedToFlexServiceWorkerRegistrationDTO =
			action.payload.eventBody as unknown as TwoFactorAuthenticationDelegatedToFlexServiceWorkerRegistrationDTO;

		navigate(
			LoginUIRoute.FLEX_SERVICE_WORKER_REGISTRATION +
			'?flexServiceWorkerRegistrationId=' + delegated.flexServiceWorkerRegistrationId
		);
	}),
	ignoreElements()
);

const loginEpics: Epic = combineEpics(
	startRememberMeAuthenticationOnRememberMeTokenSubmitted,
	notifyLoginSceneOfEmailOwnershipDeniedForFlexServiceWorkerRegistrationOnRetrievedState,
	notifyLoginSceneOfFlexServiceWorkerRegistrationFailedOnRetrievedStateWithRegistrationFailed,
	notifyLoginSceneOfFlexServiceWorkerRegistrationFailedOnStateChangeWithRegistrationFailed,
	notifyLoginSceneOfFlexServiceWorkerRegistrationCompletedOnRetrievedStateWithRegistrationCompleted,
	notifyLoginSceneOfFlexServiceWorkerRegistrationExpiredOnRetrievedStateWithRegistrationExpired,
	notifyLoginSceneOfFlexServiceWorkerRegistrationExpiredOnStateChangeWithRegistrationExpired,
	notifyLoginSceneOfRememberMeUnexpectedError,
	notifyLoginSceneOfRememberMeInputValidationError,
	notifyLoginSceneOfRememberMeAuthenticationResultRetrieved,
	startAuthenticationOnLoginCredentialsSubmitted,
	notifyLoginSceneOfCredentialsUnexpectedError,
	notifyLoginSceneOfCredentialsValidationError,
	subscribeToAuthenticationEventsOnAuthenticationStarted,
	unsubscribeFromAuthenticationEventsOnAuthenticationDelegatedToFlexServiceWorkerRegistration,
	notifyLoginSceneOfAuthenticationBlocked,
	notifyLoginSceneOfInvalidCredentialsProvided,
	notifyLoginSceneOfAuthenticationStarted,
	notifyLoginSceneOfPhoneVerificationStartFailed,
	notifyLoginSceneOfPhoneVerificationStartBlocked,
	restartPhoneVerificationWhenRequested,
	notifyLoginSceneOfAuthenticationNotFoundException,
	notifyLoginSceneOfRetryPhoneVerificationUnexpectedError,
	notifyLoginSceneOfPhoneVerificationStarted,
	notifyLoginSceneOfSecondFactorBusy,
	restartPhoneVerificationOnPhoneVerificationRestartSubmitted,
	enterVerificationCodeOnVerificationCodeSubmitted,
	notifyLoginSceneOfVerificationCodeValidationError,
	notifyLoginSceneOfPhoneVerificationResponseValidationFailed,
	notifyLoginSceneOfPhoneVerificationStopped,
	notifyLoginSceneOfPasswordChangeRequired,
	evaluateNewPasswordStrengthOnChange,
	notifyLoginSceneOfEvaluatedPasswordStrength,
	postNewPasswordOnChangePasswordSubmitted,
	notifyLoginSceneOfChangePasswordValidationError,
	notifyLoginSceneOfPasswordNotChangedWithSamePassword,
	notifyLoginSceneOfPasswordNotChangedWithInsufficientStrength,
	redirectOnSecurityContextAuthenticated,
	notifyLoginSceneOfAuthenticationExpired,
	notifyLoginSceneOfAuthenticationAborted,
	navigateToResetPasswordOnClick,
	navigateToFlexServiceWorkerRegistrationOnAuthenticationDelegatedToFlexServiceWorkerRegistration
);

export default loginEpics;
